import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { serviceLabel } from "@/components/booking/constants";
import { RegularRuns } from "@/components/RegularRuns";
import { BranchTransferButton } from "@/components/BranchTransferButton";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import RebookDialog from "@/components/RebookDialog";
import AccountRequestDialog from "@/components/AccountRequestDialog";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Package, ArrowRight, Search, Loader2, RotateCcw, Layers, Gift, Copy, Check, Undo2, Wallet, Paintbrush, Repeat } from "lucide-react";
import { toast } from "sonner";

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  useEffect(() => { refreshUser(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [orders, setOrders] = useState(null);
  const [tn, setTn] = useState("");
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const [acctStatus, setAcctStatus] = useState("none");

  useEffect(() => {
    if (user?.role === "customer" && !["florist", "on_account", "partner", "paint_shop"].includes(user?.customer_type)) {
      api.get("/account-requests/mine").then((r) => setAcctStatus(r.data.status)).catch(() => {});
    }
  }, [user]);
  const [rebookOrder, setRebookOrder] = useState(null);
  const isFlorist = user?.customer_type === "florist";
  const isAccount = ["on_account", "paint_shop"].includes(user?.customer_type);
  const isPartner = user?.customer_type === "partner";
  const isPaint = user?.customer_type === "paint_shop";
  const isNormal = user?.role === "customer" && !isFlorist && !isAccount && !isPartner;
  const bookPath = isFlorist ? "/book-flowers" : "/book";

  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])); }, []);
  useEffect(() => { api.get("/referrals/me").then((r) => setReferral(r.data)).catch(() => {}); }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referral.referral_link);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy — long-press to copy the link"); }
  };

  const active = orders?.filter((o) => o.status !== "delivered" && o.status !== "cancelled") || [];
  const past = orders?.filter((o) => o.status === "delivered" || o.status === "cancelled") || [];

  const rebook = (o) => setRebookOrder(o);
  const [runsKey, setRunsKey] = useState(0);
  const saveRun = async (o) => {
    try { await api.post("/saved-runs", { order_id: o.id }); setRunsKey((k) => k + 1); toast.success("Saved as a regular run"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const rebookReturn = (o) => navigate("/book", { state: { rebook: { pickup: o.dropoff, dropoff: o.pickup, items: o.items, service: o.service } } });

  if (isPartner) return <Navigate to="/partner" replace />;
  if (isPaint && !new URLSearchParams(window.location.search).get("courier")) return <Navigate to="/paint" replace />;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green">Your deliveries</h1>
            <p className="mt-1 text-sm text-muted-foreground">Book a courier and track your parcels in real time.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isNormal && acctStatus !== "pending" && <AccountRequestDialog onSubmitted={() => setAcctStatus("pending")} />}
            {isAccount && <BranchTransferButton />}
            {isPaint && (
              <Button size="lg" variant="outline" onClick={() => navigate("/paint")} data-testid="dashboard-paint-orders-button" className="gap-2 border-kilted-gold text-kilted-green hover:bg-kilted-gold/10">
                <Paintbrush className="h-4 w-4" /> Paint orders
              </Button>
            )}
            {isAccount && (
              <Button size="lg" variant="outline" onClick={() => navigate("/book/bulk")} data-testid="bulk-consignment-button" className="gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
                <Layers className="h-4 w-4" /> Bulk consignments
              </Button>
            )}
            <Button size="lg" onClick={() => navigate(bookPath)} data-testid="new-booking-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
              <Plus className="h-4 w-4" /> {isFlorist ? "New flower delivery" : "New booking"}
            </Button>
          </div>
        </div>

        {isNormal && acctStatus === "pending" && (
          <div className="mt-4 rounded-lg border border-kilted-gold/50 bg-kilted-gold/10 px-4 py-3 text-sm text-kilted-green" data-testid="account-pending-banner">
            Your 14-day account application is <b>pending approval</b> — Dave &amp; Jo will be in touch shortly.
          </div>
        )}
        {user?.email_verified === false && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="verify-email-banner">
            <p className="text-sm font-medium text-amber-900">Please verify your email to unlock booking — check your inbox for the link.</p>
            <Button size="sm" variant="outline" data-testid="resend-verification-button"
              onClick={() => api.post("/auth/resend-verification").then((r) => toast.success(r.data.message)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))}
              className="border-amber-400 text-amber-900 hover:bg-amber-100">
              Resend link
            </Button>
          </div>
        )}

        {referral?.credit_balance > 0 && (
          <div className="mt-4 flex items-center gap-3 rounded-lg border border-kilted-gold/50 bg-kilted-gold/15 px-4 py-3" data-testid="credit-balance-banner">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-kilted-gold/25 text-kilted-green"><Wallet className="h-4 w-4" /></div>
            <p className="text-sm text-kilted-green">
              <strong className="font-heading text-base" data-testid="credit-balance-amount">${referral.credit_balance.toFixed(2)}</strong>
              <strong> store credit</strong> is waiting on your account — it applies automatically to your next booking.
            </p>
          </div>
        )}

        {isAccount && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="account-banner">
            <strong>On-account customer.</strong> Bookings are billed to your monthly account — no upfront payment needed.
          </div>
        )}
        {isFlorist && (
          <div className="mt-4 rounded-lg border border-pink-200 bg-pink-50 px-4 py-3 text-sm text-pink-800" data-testid="florist-banner">
            <strong>Florist account.</strong> Flower deliveries are charged at a fixed flat rate.
          </div>
        )}

        {referral && (
          <Card className="mt-4 border-kilted-gold/40 bg-gradient-to-br from-kilted-gold/10 to-transparent" data-testid="referral-card">
            <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-kilted-gold/20 text-kilted-gold"><Gift className="h-5 w-5" /></div>
                <div>
                  <p className="font-heading font-bold text-kilted-green">Invite a friend, earn ${referral.reward_amount.toFixed(0)} credit</p>
                  <p className="text-sm text-muted-foreground">
                    You get <strong className="text-kilted-green">${referral.reward_amount.toFixed(0)}</strong> off each time a friend's first delivery is completed — and they get <strong className="text-kilted-green">${(referral.welcome_amount ?? 5).toFixed(0)}</strong> welcome credit too.
                    {" "}Credit balance: <strong className="text-kilted-green" data-testid="referral-credit-balance">${referral.credit_balance.toFixed(2)}</strong>
                    {referral.rewarded_count > 0 && <> · {referral.rewarded_count} reward{referral.rewarded_count === 1 ? "" : "s"} earned</>}
                    {referral.credit_balance > 0 && <> · applies automatically to your next booking</>}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Input readOnly value={referral.referral_link} data-testid="referral-link-input" className="w-full min-w-0 text-xs sm:w-64" onFocus={(e) => e.target.select()} />
                <Button variant="outline" onClick={copyLink} data-testid="referral-copy-button" className="shrink-0 gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mt-6">
          <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center">
            <p className="text-sm font-semibold">Track any parcel:</p>
            <div className="flex flex-1 gap-2">
              <Input value={tn} onChange={(e) => setTn(e.target.value)} placeholder="KC-XXXXXXXX" data-testid="dashboard-track-input" />
              <Button variant="outline" onClick={() => tn && navigate(`/track/${tn.trim().toUpperCase()}`)} data-testid="dashboard-track-button" className="gap-1.5">
                <Search className="h-4 w-4" /> Track
              </Button>
            </div>
          </CardContent>
        </Card>

        {isPaint && <RegularRuns refreshKey={runsKey} />}

        {orders === null ? (
          <div className="mt-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : orders.length === 0 ? (
          <div className="mt-16 flex flex-col items-center gap-3 text-center" data-testid="no-orders">
            <Package className="h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">No deliveries yet. Book your first parcel!</p>
            <Button onClick={() => navigate(bookPath)} className="bg-kilted-green hover:bg-kilted-greenLight">Book now</Button>
          </div>
        ) : (
          <>
            <Section title="Active" orders={active} navigate={navigate} onRebook={rebook} onReturn={rebookReturn} onSaveRun={isPaint ? saveRun : null} testid="active-orders" />
            <Section title="History" orders={past} navigate={navigate} onRebook={rebook} onReturn={rebookReturn} onSaveRun={isPaint ? saveRun : null} testid="past-orders" />
          </>
        )}
      </main>
      <RebookDialog order={rebookOrder} open={!!rebookOrder} onOpenChange={(v) => { if (!v) setRebookOrder(null); }} />
    </div>
  );
}

function Section({ title, orders, navigate, onRebook, onReturn, onSaveRun, testid }) {
  if (!orders.length) return null;
  return (
    <section className="mt-8" data-testid={testid}>
      <h2 className="mb-3 text-lg font-bold text-foreground">{title}</h2>
      <div className="grid gap-3">
        {orders.map((o) => (
          <div key={o.id} onClick={() => navigate(`/orders/${o.id}`)} data-testid={`order-card-${o.tracking_number}`}
            className="group flex cursor-pointer flex-col gap-3 rounded-lg border border-border bg-white p-4 text-left transition-transform hover:-translate-y-0.5 hover:shadow-md sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary text-kilted-green"><Package className="h-5 w-5" /></div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-heading font-bold text-kilted-green">{o.tracking_number}</span>
                  <StatusBadge status={o.status} />
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">{o.pickup.city} → {o.dropoff.city} · {o.distance_km} km · {serviceLabel(o.service)}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center justify-end gap-3">
              {o.service !== "florist" && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); onRebook(o); }} data-testid={`rebook-button-${o.tracking_number}`}
                    className="flex items-center gap-1 rounded-md border border-kilted-green/30 px-2.5 py-1.5 text-xs font-medium text-kilted-green transition-colors hover:bg-kilted-green/10">
                    <RotateCcw className="h-3.5 w-3.5" /> Rebook
                  </button>
                  {onSaveRun && (
                    <button onClick={(e) => { e.stopPropagation(); onSaveRun(o); }} data-testid={`save-run-button-${o.tracking_number}`}
                      className="flex items-center gap-1 rounded-md border border-kilted-gold/50 px-2.5 py-1.5 text-xs font-medium text-kilted-green transition-colors hover:bg-kilted-gold/10">
                      <Repeat className="h-3.5 w-3.5" /> Save run
                    </button>
                  )}
                  {o.status === "delivered" && (
                    <button onClick={(e) => { e.stopPropagation(); onReturn(o); }} data-testid={`return-button-${o.tracking_number}`}
                      title="Book the reverse trip — e.g. send an item back for a refund"
                      className="flex items-center gap-1 rounded-md border border-kilted-gold/50 px-2.5 py-1.5 text-xs font-medium text-kilted-green transition-colors hover:bg-kilted-gold/10">
                      <Undo2 className="h-3.5 w-3.5" /> Book return
                    </button>
                  )}
                </>
              )}
              <span className="font-bold text-foreground">${o.price.total.toFixed(2)}</span>
              <ArrowRight className="hidden h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 sm:block" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
